import React, { useState, useEffect } from "react";
import { Spinner, Button, Row, Col, Modal, Container } from "react-bootstrap";
import { Plus } from 'react-bootstrap-icons';
import supabase from "../../Services/Supabase";
import MenuNavbar from '../../Components/MenuNavbar';
import MenuSection from '../../Components/MenuSection';
import MenuHeader from '../../Components/MenuHeader';
import MenuFooter from '../../Components/MenuFooter';
import './Dashboard.css';

const Dashboard = () => {
  const [menu, setMenu] = useState({
    name: '',
    logo_url: '',
    subtitle: '',
    theme: 'minimale',
    sections: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        await fetchMenu(user.id);
      } else {
        setError("Utente non autenticato");
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const fetchMenu = async (userId) => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from("menu")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (fetchError) {
      setError("Menu non trovato per questo utente");
      setLoading(false);
      return;
    }

    setMenu({
      ...data,
      sections: data.sections || []
    });
    setLoading(false);
  };

  const handleFileUpload = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("logo_menu")
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from("logo_menu")
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleUpdateMenu = (field, value) => {
    setMenu(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveMenu = async () => {
    setLoading(true);
    try {
      // Filter out empty sections and items before saving
      const cleanedMenu = {
        ...menu,
        sections: menu.sections
          .filter(section => section && section.name && section.name.trim() !== '')
          .map(section => ({
            ...section,
            // Filter out items without names
            items: (section.items || []).filter(item => 
              item && item.name && item.name.trim() !== ''
            )
          }))
          // Remove sections that have no items after filtering
          .filter(section => section.items.length > 0)
      };
      
      const { error } = await supabase
        .from("menu")
        .update(cleanedMenu)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state to match saved state
      setMenu(cleanedMenu);
      
      localStorage.setItem('forceReloadMain', new Date().getTime().toString());
      
    } catch (error) {
      setError(`Errore nel salvataggio: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = () => {
    const newSection = {
      id: Date.now().toString(),
      name: '', // Empty name
      items: [],
      isNew: true // Flag for unsaved sections
    };

    setMenu(prevMenu => ({
      ...prevMenu,
      sections: [...(prevMenu?.sections || []), newSection]
    }));

    setTimeout(() => {
      const sections = document.querySelectorAll('.menu-section');
      if (sections.length > 0) {
        const lastSection = sections[sections.length - 1];
        lastSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleRemoveSection = (sectionId) => {
    if (!menu || !sectionId) return;

    const section = menu.sections.find(s => s.id === sectionId);
    
    // If section is new and empty, just remove it without saving
    if (section?.isNew && (!section.items || section.items.length === 0)) {
      setMenu(prevMenu => ({
        ...prevMenu,
        sections: prevMenu.sections.filter(s => s.id !== sectionId)
      }));
      return;
    }

    setMenu(prevMenu => ({
      ...prevMenu,
      sections: (prevMenu?.sections || []).filter(section => 
        section && section.id !== sectionId
      )
    }));
  };

  const handleUpdateSection = (sectionId, field, value) => {
    if (!sectionId) return;

    setMenu(prevMenu => ({
      ...prevMenu,
      sections: (prevMenu?.sections || []).map(section => {
        if (!section || section.id !== sectionId) return section;
        
        // If setting a name, remove isNew flag
        if (field === 'name' && value) {
          const { isNew, ...sectionWithoutFlag } = section;
          return { ...sectionWithoutFlag, [field]: value };
        }
        
        return { ...section, [field]: value };
      })
    }));
  };

  const handleAddItem = (sectionId) => {
    if (!menu || !sectionId) return;

    setMenu(prevMenu => {
      if (!prevMenu || !prevMenu.sections) return prevMenu;

      const updatedSections = prevMenu.sections.map(section => {
        if (!section || section.id !== sectionId) return section;

        // Remove isNew flag when adding first item
        const { isNew, ...sectionWithoutFlag } = section;
        
        return {
          ...sectionWithoutFlag,
          items: [
            ...(section.items || []),
            {
              id: Date.now(), // Genera un ID univoco
              name: '',
              price: '',
              description: '',
              allergens: []
            }
          ]
        };
      });

      return {
        ...prevMenu,
        sections: updatedSections
      };
    });
  };

  const handleUpdateItem = (sectionId, itemId, field, value) => {
    if (!menu || !sectionId || !itemId) {
      console.error('Dati mancanti:', { sectionId, itemId });
      return;
    }

    setMenu(prevMenu => {
      if (!prevMenu?.sections) return prevMenu;

      const updatedSections = prevMenu.sections.map(section => {
        if (!section || section.id !== sectionId) return section;

        const updatedItems = (section.items || []).map(item => {
          if (!item || item.id !== itemId) return item;
          return { ...item, [field]: value };
        });

        return { ...section, items: updatedItems };
      });

      return { ...prevMenu, sections: updatedSections };
    });
  };

  const handleRemoveItem = (sectionId, itemId) => {
    if (!menu || !sectionId || !itemId) return;

    setMenu(prevMenu => ({
      ...prevMenu,
      sections: (prevMenu?.sections || []).map(section => {
        if (!section || section.id !== sectionId) return section;
        
        return {
          ...section,
          items: (section.items || []).filter(item => 
            item && item.id !== itemId
          )
        };
      })
    }));
  };

  const handleMoveSection = async (currentIndex, direction) => {
    const newSections = [...menu.sections];
    let newIndex;
    
    switch (direction) {
      case 'left':
      case 'up':
        newIndex = currentIndex > 0 ? currentIndex - 1 : newSections.length - 1;
        break;
      case 'right':
      case 'down':
        newIndex = currentIndex < newSections.length - 1 ? currentIndex + 1 : 0;
        break;
      default:
        return;
    }
    
    // Swap sections
    [newSections[currentIndex], newSections[newIndex]] = 
    [newSections[newIndex], newSections[currentIndex]];
    
    setMenu(prevMenu => ({
      ...prevMenu,
      sections: newSections
    }));

    // Save changes
    try {
      await supabase
        .from("menu")
        .update({ sections: newSections })
        .eq('user_id', user.id);

      localStorage.setItem('forceReloadMain', Date.now().toString());
    } catch (error) {
      console.error('Error saving menu:', error);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'header':
        return (
          <MenuHeader
            menu={menu}
            onUpdateMenu={handleUpdateMenu}
            onFileUpload={handleFileUpload}
            user={user}
            onImageClick={() => setShowImageModal(true)}
          />
        );
      case 'menu':
        return (
          <Container fluid className="dashboard-container">
            <Row className="mb-4">
              <Col>
                <h1 className="text-center display-4 mb-5">Gestione Menu</h1>
              </Col>
            </Row>
            
            <div className="dashboard-content">
              <Row>
                <Col>
                  {menu?.sections?.length > 0 ? (
                    <Row className="g-4">
                      {menu.sections.map((section, index) => (
                        section && (
                          <Col key={section.id || Date.now()} lg={6} xl={4}>
                            <MenuSection
                              section={section}
                              index={index}
                              totalSections={menu.sections.length}
                              onMoveSection={handleMoveSection}
                              onUpdateSection={handleUpdateSection}
                              onRemoveSection={handleRemoveSection}
                              onAddItem={handleAddItem}
                              onUpdateItem={handleUpdateItem}
                              onRemoveItem={handleRemoveItem}
                              onFileUpload={handleFileUpload}
                            />
                          </Col>
                        )
                      ))}
                    </Row>
                  ) : null}
                </Col>
              </Row>
            </div>

            {/* Fixed Add Button */}
            <Button 
              variant="primary" 
              onClick={handleAddSection}
              className="fixed-add-button"
              title="Aggiungi nuova sezione"
            >
              <Plus size={20} />
              <span>Sezione</span>
            </Button>
          </Container>
        );
      case 'footer':
        return (
          <div className="p-4">
            <MenuFooter menu={menu} onSave={handleSaveMenu} />
          </div>
        );
      default:
        return (
          <div className="text-center py-5 text-muted">
            <h3>Seleziona una sezione dal menu laterale</h3>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }
  
  if (error) {
    return <div className="dashboard-error alert alert-danger">{error}</div>;
  }

  return (
    <div className="dashboard-wrapper">
      <MenuNavbar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
      />
      
      <div className="dashboard-container">
        {/* Dynamic Content Area */}
        <main className="dashboard-content">
          <div className={`content-wrapper ${activeSection ? 'active' : ''}`}>
            {!activeSection ? <WelcomeScreen /> : renderContent()}
          </div>
        </main>
      </div>

      <MenuFooter menu={menu} onSave={handleSaveMenu} />

      {/* Image Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Logo del Menu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {menu.logo_url && (
            <img 
              src={menu.logo_url} 
              alt="Logo" 
              className="img-fluid"
              style={{ maxHeight: '400px' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

const WelcomeScreen = () => (
  <div className="welcome-screen text-center">
    <h1 className="display-4 mb-4">Benvenuto nel tuo Dashboard</h1>
    <p className="lead mb-4">Seleziona una sezione dal menu per iniziare</p>
    <div className="welcome-icons mb-4">
      {/* Add some decorative icons or illustrations here */}
    </div>
  </div>
);

export default Dashboard;